import React, { useState, useCallback } from "react";

import { Stack } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";

import { RunningMessage } from "@/utils/api";
import { useRunningMessages } from "@/hooks";

import { DashboardLayout } from "@/components/layouts";

import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
} from "@/components/generic";
import {
  RunningMessageFormModal,
  RunningMessagesTable,
  AssignRunningMessageModal,
} from "@/components/features";
import { DEFAULT_PAGE_SIZE } from "@/config";

export const RunningMessagesPage: React.FC<PageProps> = (props: PageProps) => {
  //const { selectedVenue, setSelectedVenue } = useVenuesContext();

  // Use the running messages hook for all data management
  const {
    // Data state
    runningMessages,
    loading,
    loadingMore,
    hasMore,
    isEmpty,

    // Search and filters
    sortBy,

    // Operations state
    isCreating,
    isUpdating,
    removingMessageId,
    selectedMessage,

    // Data operations
    list,
    loadMore,
    refresh,

    // Search and filter operations
    //setFilters,
    setSortBy,

    // CRUD operations
    create,
    update,
    remove,

    // UI state management
    setSelectedMessage,

    // Error handling
    lastError,
    clearError,
  } = useRunningMessages({
    initialFilters: {
      //venueId: selectedVenue?.id ? [selectedVenue.id] : undefined,
    },
    autoLoad: true,
  });

  // Local UI state
  const [formModalOpened, setFormModalOpened] = useState<boolean>(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);
  const [assignModalOpened, setAssignModalOpened] = useState<boolean>(false);
  const [messageToDelete, setMessageToDelete] = useState<RunningMessage | null>(
    null
  );
  const [messageToAssign, setMessageToAssign] = useState<RunningMessage | null>(
    null
  );
  const [searchKeyword, setSearchKeyword] = useState<string>("");

  // Handle venue selection change
  // const handleVenueChange = useCallback(
  //   (venueId: string | null) => {
  //     if (venueId && selectedVenue?.id !== venueId) {
  //       // Find and set the venue object - this is a simplified approach
  //       // In a real app, you might want to fetch the venue object by ID
  //       // For now, we'll just update with the ID and let the context handle it
  //       setSelectedVenue({ id: venueId } as any);

  //       // Update filters to load messages for selected venue
  //       setFilters({
  //         venueId: venueId ? [venueId] : undefined,
  //         messageText: searchKeyword || undefined,
  //       });
  //     } else if (!venueId) {
  //       setSelectedVenue(null);
  //       setFilters({
  //         messageText: searchKeyword || undefined,
  //       });
  //     }
  //   },
  //   [selectedVenue?.id, setSelectedVenue, setFilters, searchKeyword]
  // );

  // Handle search input changes
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchKeyword(value);

      // Load messages with new search term and current venue filter
      const newFilters = {
        //venueId: selectedVenue?.id ? [selectedVenue.id] : undefined,
        searchKeyword: value || undefined,
      };
      list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    },
    [/*selectedVenue?.id,*/ list, sortBy]
  );

  // Handle add message button click
  const handleAddMessage = useCallback(() => {
    setSelectedMessage(null);
    setFormModalOpened(true);
  }, [setSelectedMessage]);

  // Handle edit message
  const handleEditMessage = useCallback(
    (message: RunningMessage) => {
      setSelectedMessage(message);
      setFormModalOpened(true);
    },
    [setSelectedMessage]
  );

  // Handle duplicate message
  const handleDuplicateMessage = useCallback(
    (message: RunningMessage) => {
      setSelectedMessage({ ...message, id: "" });
      setFormModalOpened(true);
    },
    [setSelectedMessage]
  );

  // Handle assign content block to displays
  const handleAssignMessage = useCallback((runningMessage: RunningMessage) => {
    setMessageToAssign(runningMessage);
    setAssignModalOpened(true);
  }, []);

  // Handle assign modal close
  const handleAssignModalClose = useCallback(() => {
    setAssignModalOpened(false);
    setMessageToAssign(null);
  }, []);

  // Handle remove message button click
  const handleRemoveMessageClick = useCallback((message: RunningMessage) => {
    setMessageToDelete(message);
    setDeleteModalOpened(true);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (messageToDelete && messageToDelete.id) {
      const success = await remove({ id: messageToDelete.id });
      if (success) {
        setDeleteModalOpened(false);
        setMessageToDelete(null);
      }
      // Error handling is managed by the hook
    }
  }, [messageToDelete, remove]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setDeleteModalOpened(false);
    setMessageToDelete(null);
  }, []);

  // Handle form modal close
  const handleFormModalClose = useCallback(() => {
    setFormModalOpened(false);
    setSelectedMessage(null);
    clearError();
  }, [setSelectedMessage, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle create message
  const handleCreateMessage = useCallback(
    async (messageData: any) => {
      const result = await create(messageData);
      return result;
    },
    [create]
  );

  // Handle update message
  const handleUpdateMessage = useCallback(
    async (message: RunningMessage, messageData: any) => {
      if (message.id) {
        const result = await update({ id: message.id }, messageData);
        return result;
      }
      return null;
    },
    [update]
  );

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Page toolbar with venue selector, actions and search */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "venue-selector",
              items: [
                // <VenueSelector
                //   key="venue-selector"
                //   value={selectedVenue?.id || null}
                //   onChange={handleVenueChange}
                //   placeholder="Select venue to manage messages"
                //   style={{ minWidth: 250 }}
                //   required
                //   disabled={isCreating || !!removingMessageId || loading}
                // />,
                <AppButton
                  key="add-message"
                  iconName="Plus"
                  onClick={handleAddMessage}
                  disabled={isCreating || !!removingMessageId || loading}
                >
                  Add
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={isCreating || !!removingMessageId || loading}
                >
                  Refresh
                </AppButton>,
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "search",
              items: [
                <SearchInput
                  key="search"
                  placeholder="Search messages..."
                  value={searchKeyword}
                  onChange={(value) => handleSearchChange(value)}
                  style={{ minWidth: 250 }}
                />,
              ],
              align: "right",
            },
          ]}
        />

        {/* Running messages table */}
        <RunningMessagesTable
          runningMessages={runningMessages}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          sortBy={sortBy}
          onSortChange={setSortBy}
          onLoadMore={loadMore}
          onEditMessage={handleEditMessage}
          onDuplicateMessage={handleDuplicateMessage}
          onRemoveMessage={handleRemoveMessageClick}
          onAssignMessage={handleAssignMessage}
          removingMessageId={removingMessageId}
          size="md"
        />

        {/* Empty state when no messages exist */}
        <EmptyState
          display={isEmpty && !loading}
          title={"No running messages yet"}
          description={"Start by adding your first running message"}
          actionIcon="Plus"
          actionLabel="Add Running Message"
          onAction={handleAddMessage}
        />
      </Stack>

      {/* Running message form modal for create/edit */}
      <RunningMessageFormModal
        opened={formModalOpened}
        onClose={handleFormModalClose}
        runningMessage={selectedMessage}
        onCreateMessage={handleCreateMessage}
        onUpdateMessage={handleUpdateMessage}
        isCreating={isCreating}
        isUpdating={isUpdating}
        lastError={lastError}
        onClearError={clearError}
        //venueId={selectedVenue?.id || ""}
      />

      {/* Assign content block to displays modal */}
      {messageToAssign && (
        <AssignRunningMessageModal
          opened={assignModalOpened}
          onClose={handleAssignModalClose}
          runningMessage={messageToAssign}
          //venueId={selectedVenue?.id}
        />
      )}

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={deleteModalOpened}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Running Message"
        message={`Are you sure you want to delete "${
          messageToDelete?.name || messageToDelete?.messageText
        }"?`}
        details="This action cannot be undone. The running message will be permanently removed from all displays."
        confirmInProgress={removingMessageId === messageToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
